import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { copy } from '../../config/copy';
import { useReposts } from '../../stores/reposts';
import { useSession } from '../../stores/session';
import { RewindComposer } from './RewindComposer';

interface Props {
  trackId: number;
  /**
   * What is being rewound, for the composer to name.
   *
   * Optional because the button lives in rows that do not always have it to
   * hand; the composer simply says less when it is missing rather than
   * refusing to open.
   */
  title?: string;
  artist?: string | null;
  /** What the server last said. The store's overlay wins if it has an opinion. */
  reposted?: boolean;
  repostCount?: number;
  showCount?: boolean;
  size?: 'sm' | 'md';
  /** Rewinding a private or unlisted track is refused; do not offer it. */
  disabled?: boolean;
  className?: string;
}

/**
 * The rewind button.
 *
 * ## Why it looks nothing like the heart
 *
 * A favourite is private appreciation; a rewind is publishing — it puts
 * somebody else's track in front of everybody who follows you. Two acts that
 * different should not be two identically-shaped icons sitting next to each
 * other, or people will press the wrong one and only find out from their
 * followers.
 *
 * So it is a loop rather than a heart, and it turns Fresh rather than Hot.
 */
export function RepostButton({
  trackId,
  title,
  artist,
  reposted,
  repostCount = 0,
  showCount = false,
  size = 'md',
  disabled = false,
  className = '',
}: Props) {
  const navigate = useNavigate();
  const user = useSession((state) => state.user);
  const [composing, setComposing] = useState(false);

  // Subscribed to the maps rather than calling a resolver, so this re-renders
  // when its own track changes and not when any other one does.
  const overlay = useReposts((state) => state.overlay[trackId]);
  const overlayCount = useReposts((state) => state.counts[trackId]);
  const pending = useReposts((state) => state.pending[trackId] ?? false);
  const toggle = useReposts((state) => state.toggle);

  const isReposted = overlay ?? reposted ?? false;
  const count = overlayCount ?? repostCount;

  const dimension = size === 'sm' ? 'h-8 w-8' : 'h-9 w-9';
  const glyph = size === 'sm' ? 'h-4 w-4' : 'h-[18px] w-[18px]';

  return (
    <>
      {composing && (
        <RewindComposer
          title={title ?? 'this track'}
          artist={artist ?? null}
          pending={pending}
          onCancel={() => setComposing(false)}
          onRewind={(caption) => {
            void toggle(trackId, false, count, caption);
            setComposing(false);
          }}
        />
      )}

    <button
      type="button"
      aria-pressed={isReposted}
      aria-label={isReposted ? copy['action.unrepost'] : copy['action.repost']}
      title={disabled ? 'Only public tracks can be rewound' : undefined}
      data-repost-button={trackId}
      data-reposted={isReposted ? 'true' : 'false'}
      disabled={pending || disabled}
      onClick={(event) => {
        // Rewind buttons sit inside rows and cards that are themselves links.
        event.preventDefault();
        event.stopPropagation();

        if (!user) {
          navigate('/login');

          return;
        }

        /*
         * Rewinding asks; taking one back does not.
         *
         * This is publishing - see `RewindComposer` - and it should be
         * deliberate. Withdrawing is the opposite: nobody needs a dialog to
         * take back something they have already thought better of, and making
         * the undo harder than the do is how people end up stuck with it.
         */
        if (isReposted) {
          void toggle(trackId, true, count);

          return;
        }

        setComposing(true);
      }}
      className={[
        'group inline-flex items-center gap-1.5 rounded-[var(--radius-pill)] transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        showCount ? 'px-2' : '',
        className,
      ].join(' ')}
    >
      <span
        className={[
          'inline-flex items-center justify-center rounded-[var(--radius-pill)] transition-colors',
          dimension,
          isReposted ? 'text-positive' : 'text-soft group-hover:text-ink',
        ].join(' ')}
      >
        <svg
          viewBox="0 0 20 20"
          className={glyph}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {/* A loop: round the top, back round the bottom. The state reads from
              colour *and* from the filled arrowheads, so it survives being
              seen by somebody who cannot tell green from grey. */}
          <path d="M4 8.5V7a2.5 2.5 0 0 1 2.5-2.5H14" />
          <path d={isReposted ? 'M11.5 2l2.5 2.5-2.5 2.5z' : 'M11.5 2L14 4.5l-2.5 2.5'} fill={isReposted ? 'currentColor' : 'none'} />
          <path d="M16 11.5V13a2.5 2.5 0 0 1-2.5 2.5H6" />
          <path d={isReposted ? 'M8.5 18L6 15.5 8.5 13z' : 'M8.5 18L6 15.5 8.5 13'} fill={isReposted ? 'currentColor' : 'none'} />
        </svg>
      </span>

      {showCount && (
        <span className="meta-caps tabular-nums" data-repost-count={trackId}>
          {count.toLocaleString()}
        </span>
      )}
    </button>
    </>
  );
}
